import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "../components/Skeleton.js";
import { SessionDetailModal } from "../components/SessionDetailModal.js";
import { DownloadButton } from "../components/DownloadButton.js";

interface Row {
  id: number;
  ocppTransactionId: number;
  cpId: string;
  chargerLabel: string | null;
  connectorId: number;
  idTag: string;
  chassiVin: string | null;
  vinAttributionSource: string | null;
  startTime: string;
  stopTime: string | null;
  energyWh: number | null;
  stopReason: string | null;
}

type StatusFilter = "all" | "open" | "closed";

function fmt(iso: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtEnergy(wh: number | null): string {
  if (wh == null) return "";
  return (wh / 1000).toFixed(3);
}

function fmtDuration(start: string, stop: string | null): string {
  if (!stop) return "em curso";
  const minutes = Math.max(0, Math.round((new Date(stop).getTime() - new Date(start).getTime()) / 60_000));
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}min`;
}

function toCsv(rows: Row[]): string {
  const header = [
    "transactionId",
    "cpId",
    "connectorId",
    "idTag",
    "vehicleVin",
    "durationMinutes",
    "startTime",
    "stopTime",
    "energyKwh",
    "stopReason",
  ];
  const esc = (v: unknown) => {
    const s = String(v ?? "");
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(";")];
  for (const r of rows) {
    lines.push(
      [
        r.ocppTransactionId,
        r.cpId,
        r.connectorId,
        r.idTag,
        r.chassiVin ?? "",
        r.stopTime ? Math.max(0, Math.round((new Date(r.stopTime).getTime() - new Date(r.startTime).getTime()) / 60_000)) : "",
        r.startTime,
        r.stopTime ?? "",
        fmtEnergy(r.energyWh),
        r.stopReason ?? "",
      ]
        .map(esc)
        .join(";"),
    );
  }
  return lines.join("\r\n");
}

export function SessionsPage() {
  // null = ainda não fez fetch (skeleton); [] = fetch ok, sem linhas
  // (mensagem "nenhuma sessão"). Sem essa distinção o "Nenhuma sessão"
  // aparece por um instante em cada troca de filtro.
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cpId, setCpId] = useState("");
  const [vehicleVin, setVehicleVin] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // ID interno (não OCPP) da sessão aberta no modal. null = modal fechado.
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (cpId) p.set("cpId", cpId);
    if (vehicleVin) p.set("vehicleVin", vehicleVin);
    if (from) p.set("from", new Date(from).toISOString());
    if (to) p.set("to", new Date(to).toISOString());
    if (status !== "all") p.set("status", status);
    p.set("limit", "500");
    return p.toString();
  }, [cpId, vehicleVin, status, from, to]);

  // URL do export XLSX. O endpoint exige `from` e `to`; se o operador não
  // preencheu, ficamos com `null` e o botão vai desabilitado com título
  // explicativo. `chargerId` na API corresponde ao `cpId` do UI.
  const xlsxHref = useMemo(() => {
    if (!from || !to) return null;
    const p = new URLSearchParams();
    p.set("from", new Date(from).toISOString());
    p.set("to", new Date(to).toISOString());
    if (cpId) p.set("chargerId", cpId);
    if (vehicleVin) p.set("vehicleVin", vehicleVin);
    return `/api/reports/xlsx/sessions?${p.toString()}`;
  }, [from, to, cpId, vehicleVin]);

  const xlsxFilename = useMemo(() => {
    // Fallback local; o servidor manda Content-Disposition próprio.
    const f = from ? new Date(from).toISOString().slice(0, 10) : "inicio";
    const t = to ? new Date(to).toISOString().slice(0, 10) : "fim";
    return `nxvolt-sessions-${f}_${t}.xlsx`;
  }, [from, to]);

  const pdfHref = useMemo(() => {
    if (!from || !to) return null;
    const p = new URLSearchParams();
    p.set("from", new Date(from).toISOString());
    p.set("to", new Date(to).toISOString());
    if (cpId) p.set("chargerId", cpId);
    if (vehicleVin) p.set("vehicleVin", vehicleVin);
    return `/api/reports/pdf/sessions?${p.toString()}`;
  }, [from, to, cpId, vehicleVin]);

  const pdfFilename = useMemo(() => {
    const f = from ? new Date(from).toISOString().slice(0, 10) : "inicio";
    const t = to ? new Date(to).toISOString().slice(0, 10) : "fim";
    return `nxvolt-sessions-${f}_${t}.pdf`;
  }, [from, to]);

  useEffect(() => {
    setError(null);
    setRows(null); // dispara skeleton em toda troca de filtro
    fetch("/api/transactions?" + query, { credentials: "include" })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
      )
      .then((d: { transactions: Row[] }) => setRows(d.transactions))
      .catch((e: Error) => setError(e.message));
  }, [query]);

  function download() {
    const blob = new Blob([toCsv(rows ?? [])], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.download = `nxvolt-sessions-${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const loaded = rows ?? [];
  const totalKwh = loaded.reduce((s, r) => s + Number(r.energyWh ?? 0), 0) / 1000;
  const closedCount = loaded.filter((r) => r.stopTime).length;

  // Subtítulo: descreve o "consumo do período selecionado" com números
  // reais. Enquanto está carregando, um traço curto — mentir número
  // parcial deixa o operador tomar decisão sobre dado incompleto.
  const sub =
    rows == null
      ? "Carregando sessões do período selecionado…"
      : `${loaded.length} ${loaded.length === 1 ? "sessão" : "sessões"} · ${closedCount} concluídas · ${totalKwh.toFixed(2)} kWh`;

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Histórico</span>
          <h1>Sessões</h1>
          <div className="nx-page-head__sub">{sub}</div>
        </div>
        <div
          className="nx-page-head__actions"
          style={{ display: "flex", gap: "var(--nx-space-2)", flexWrap: "wrap" }}
        >
          <button
            className="nx-btn nx-btn--ghost-dark"
            type="button"
            onClick={download}
            disabled={!rows || rows.length === 0}
          >
            ⤓ CSV
          </button>
          <DownloadButton
            href={xlsxHref ?? "#"}
            filename={xlsxFilename}
            label="Exportar XLSX"
            variant="primary"
            disabled={!xlsxHref}
            title={
              xlsxHref
                ? "Baixa as sessões do período com os filtros atuais em XLSX"
                : "Selecione as datas de início e fim para exportar"
            }
          />
          <DownloadButton
            href={pdfHref ?? "#"}
            filename={pdfFilename}
            label="Exportar PDF"
            variant="secondary"
            disabled={!pdfHref}
            title={
              pdfHref
                ? "Baixa o histórico do período com os filtros atuais em PDF"
                : "Selecione as datas de início e fim para exportar"
            }
          />
        </div>
      </div>

      <div className="nx-card" style={{ marginBottom: "var(--nx-space-4)" }}>
        <div className="nx-filters">
          <label className="nx-field">
            <span>ID do carregador</span>
            <input
              type="text"
              placeholder="ex: NX-SIM-01"
              value={cpId}
              onChange={(e) => setCpId(e.target.value)}
            />
          </label>
          <label className="nx-field">
            <span>Veículo (VIN)</span>
            <input type="text" placeholder="VIN atribuído à sessão" value={vehicleVin} onChange={(e) => setVehicleVin(e.target.value)} />
          </label>
          <label className="nx-field">
            <span>De</span>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="nx-field">
            <span>Até</span>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <label className="nx-field">
            <span>Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <option value="all">Todas</option>
              <option value="closed">Concluídas</option>
              <option value="open">Em andamento</option>
            </select>
          </label>
        </div>
      </div>

      {error && (
        <div className="nx-error" style={{ marginBottom: "var(--nx-space-4)" }}>
          {error}
        </div>
      )}

      <div className="nx-card">
        {rows != null && (
          <p
            style={{
              color: "var(--nx-mute)",
              margin: "0 0 var(--nx-space-3) 0",
            }}
          >
            <strong className="mono">{loaded.length}</strong> sessões ·
            concluídas: <strong className="mono">{closedCount}</strong> · total{" "}
            <strong className="mono">{totalKwh.toFixed(3)}</strong> kWh
          </p>
        )}
        <div className="nx-table-wrap">
          <table className="nx-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Carregador</th>
                <th>Conector</th>
                <th>Cartão</th>
                <th>Veículo</th>
                <th>Início</th>
                <th>Fim</th>
                <th>Duração</th>
                <th>Energia (kWh)</th>
                <th>Motivo</th>
                <th aria-label="Recibo">Recibo</th>
              </tr>
            </thead>
            <tbody>
              {rows == null ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={`skel-${i}`}>
                    <td colSpan={11} style={{ padding: "8px 12px" }}>
                      <Skeleton variant="row" height={24} />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    style={{ color: "var(--nx-mute)", textAlign: "center" }}
                  >
                    Nenhuma sessão encontrada com os filtros atuais.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">#{r.ocppTransactionId}</td>
                    <td className="mono" title={r.cpId}>{r.chargerLabel ?? r.cpId}</td>
                    <td className="mono">{r.connectorId}</td>
                    <td className="mono">{r.idTag}</td>
                    <td className="mono" title={r.vinAttributionSource ?? undefined}>{r.chassiVin ?? "—"}</td>
                    <td>{fmt(r.startTime)}</td>
                    <td>{r.stopTime ? fmt(r.stopTime) : <em>em curso</em>}</td>
                    <td>{fmtDuration(r.startTime, r.stopTime)}</td>
                    <td className="mono">{fmtEnergy(r.energyWh)}</td>
                    <td>{r.stopReason ?? "—"}</td>
                    <td>
                      {/* Antes o botão era "Recibo" (baixa PDF direto).
                          Trocado por "Ver detalhes" que abre modal com
                          timeline de fases (bulk / absorção / finalização),
                          gráficos de potência e SOC, KPIs por estágio e o
                          botão de PDF como ação secundária. */}
                      <button
                        type="button"
                        onClick={() => setSelectedSessionId(r.id)}
                        className="nx-btn nx-btn--sm nx-btn--secondary"
                        title={`Ver detalhes da sessão #${r.ocppTransactionId}`}
                      >
                        Ver detalhes
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {selectedSessionId !== null && (
        <SessionDetailModal
          sessionId={selectedSessionId}
          onClose={() => setSelectedSessionId(null)}
        />
      )}
    </div>
  );
}
